// services-context.tsx — the React half of the service registry.
//
// `./services` is the pure, node-testable half: types, the SERVICES
// table, and functions that take a ServicesConfig. This file owns the
// one piece that needs React and the network: fetching that config and
// handing it to the tree.
//
// The contract with the backend is deliberately tiny:
//
//     GET /api/services  →  { "services": ["translator", "settings", …] }
//
// and the contract with the user is smaller still: **reads that do not
// succeed put the app back on DEFAULT_SERVICES** — translator +
// settings + admin, exactly what shipped before F-01. A 404 (the state
// of production today, since nayto-core doesn't exist yet), a network
// error, a 500, an HTML error page, a body of the wrong shape: all of
// them lead to the floor, not to an error screen. No toast, no empty
// nav, no spinner — a deployment with no /api/services never notices
// this file exists.
//
// Fail-closed, with a tolerance counter (F-02). A backend that has
// stopped answering must never leave a service switched on — but F-01's
// one-strike version was twitchy: a single 30s hiccup in nayto-core
// pulled an enabled service out from under every open tab and restored
// it a minute later. It now takes SERVICES_FAILURE_TOLERANCE (3)
// consecutive failures to drop, and one good read to forgive the
// streak. The policy itself lives in `applyServicesRead` in ./services,
// as a pure function, so it is tested without React or a clock.
//
// Why a poll and not a one-shot: switching a service on is meant to be
// an ops action, not a deploy. 60s is the compromise between "the admin
// flipped it and nothing happened" and hammering the API from every
// open tab.

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { UserRole } from '@nayto/auth';
import { apiFetchQuiet } from '@/auth';
import {
  DEFAULT_SERVICES,
  INITIAL_SERVICES_STATE,
  SERVICES_POLL_INTERVAL_MS,
  applyServicesRead,
  enabledAdminTabs,
  enabledOnboardingSteps,
  enabledRoutes,
  fetchServicesConfig,
  isRouteEnabled,
  isServiceEnabled,
  isSettingsSectionEnabled,
  visibleRoutesFor,
  type AdminTabKey,
  type OnboardingStep,
  type Route,
  type ServiceId,
  type ServicesConfig,
  type ServicesReadState,
  type SettingsSection,
} from '@/services';

const ServicesContext = createContext<ServicesConfig>(DEFAULT_SERVICES);

interface ServicesProviderProps {
  children: ReactNode;
  /** Test / Storybook seam: skip the network and pin a config. */
  value?: ServicesConfig;
}

export function ServicesProvider({ children, value }: ServicesProviderProps) {
  const [state, setState] = useState<ServicesReadState>(
    value ? { config: value, consecutiveFailures: 0 } : INITIAL_SERVICES_STATE,
  );

  useEffect(() => {
    if (value) return; // pinned — never poll
    let cancelled = false;

    const load = async () => {
      // `apiFetchQuiet`, not `apiFetch`: a 401 from a config endpoint
      // must never sign the user out of the translator. See
      // ./auth/session for the two instances.
      const next = await fetchServicesConfig(apiFetchQuiet);
      if (cancelled) return;
      // The whole policy — including the failure streak — is in
      // applyServicesRead. This component just holds the state.
      setState((prev) => applyServicesRead(prev, next));
    };

    void load();
    const id = window.setInterval(() => void load(), SERVICES_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [value]);

  return <ServicesContext.Provider value={state.config}>{children}</ServicesContext.Provider>;
}

/** The live config. Every other hook here is sugar over this one. */
export function useServices(): ServicesConfig {
  return useContext(ServicesContext);
}

export function useIsServiceEnabled(id: ServiceId): boolean {
  return isServiceEnabled(useServices(), id);
}

export function useIsRouteEnabled(route: Route): boolean {
  return isRouteEnabled(useServices(), route);
}

export function useEnabledRoutes(): ReadonlySet<Route> {
  const config = useServices();
  return useMemo(() => enabledRoutes(config), [config]);
}

export function useVisibleRoutes(role: UserRole | undefined): ReadonlySet<Route> {
  const config = useServices();
  return useMemo(
    () => (role ? visibleRoutesFor(config, role) : new Set<Route>()),
    [config, role],
  );
}

export function useEnabledAdminTabs(): readonly AdminTabKey[] {
  const config = useServices();
  return useMemo(() => enabledAdminTabs(config), [config]);
}

export function useEnabledOnboardingSteps(): readonly OnboardingStep[] {
  const config = useServices();
  return useMemo(() => enabledOnboardingSteps(config), [config]);
}

export function useIsSettingsSectionEnabled(section: SettingsSection): boolean {
  return isSettingsSectionEnabled(useServices(), section);
}
